import { NavLink } from "react-router";
import cn from "classnames";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IconName, IconProp } from "@fortawesome/fontawesome-svg-core";
import { OnboardingStatus, User, USER_STATUS } from "../types/User";
import { BuildingServiceType, ServiceTypeEnum } from "../types/Building";
import useGetOnboardingAmenitiesEnabled from "../hooks/useGetOnboardingAmenities";
import { useServiceDisplayName } from "../hooks/useServiceDisplayName";
import { useFeatureToggle } from "@/routes/features";
import { encodeId } from "@/utils/idObfuscation";
import { isStandaloneFutureResident } from "@/utils/standaloneRedirect";

export default function MainMenu({
  user,
  onboardingData
}: {
  user: User;
  onboardingData: OnboardingStatus[];
}) {
  const menuItems = useGetMenuItems(user, onboardingData);

  return (
    <nav className="flex-1 px-2 space-y-1 overflow-auto bg-white">
      {menuItems
        .filter((mi) => !mi.excludeFromMainMenu)
        .map((item) => (
          <NavItem
            key={item.to}
            label={item.label}
            to={item.to}
            icon={item.icon}
            image={item.image}
          />
        ))}
    </nav>
  );
}

type NavItemProps = {
  label: string;
  to: string;
  icon: IconProp;
  excludeFromMainMenu?: boolean;
  image?: string;
};

function NavItem({ label, to, icon, image }: NavItemProps) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        cn(
          isActive
            ? "bg-gray-100 text-[#F9877E]"
            : "text-gray-600 hover:bg-gray-50 hover:text-gray-900",
          "group flex items-center px-2 py-2 text-sm font-medium rounded-md"
        )
      }
    >
      <span className="flex justify-center w-6">
        {image ? (
          <img src={image} className="h-5 w-5" />
        ) : (
          <FontAwesomeIcon icon={icon} />
        )}
      </span>
      <span className="inline-block ml-2">{label}</span>
    </NavLink>
  );
}

export function useGetMenuItems(
  user: User,
  onboardingData: OnboardingStatus[] = []
) {
  const { serviceTypes = [] } = user.building || {};
  const biltRewardsFeatureToggle = useFeatureToggle("bilt-rewards");

  let navItems: NavItemProps[] = [];

  const { enabled: isRentEnabled = false, displayName: rentDisplayName } =
    serviceTypes.find(
      (service: BuildingServiceType) =>
        service.serviceType === ServiceTypeEnum.Rent
    ) || {};
  const { enabled: isAmenitiesEnabled = false } =
    serviceTypes.find(
      (service: BuildingServiceType) =>
        service.serviceType === ServiceTypeEnum.Amenities
    ) || {};
  const { enabled: isEventsEnabled = false } =
    serviceTypes.find(
      (service: BuildingServiceType) =>
        service.serviceType === ServiceTypeEnum.Events
    ) || {};
  const eventsDisplayName = useServiceDisplayName(
    serviceTypes,
    ServiceTypeEnum.Events,
    "Events"
  );
  const {
    enabled: isBuildingOffersEnabled = false,
    displayName: marketPlaceDisplayName
  } =
    serviceTypes.find(
      (service: BuildingServiceType) =>
        service.serviceType === ServiceTypeEnum.MarketPlace
    ) || {};
  const {
    enabled: isMaintenanceEnabled = false,
    displayName: maintenanceDisplayName
  } =
    serviceTypes.find(
      (service: BuildingServiceType) =>
        service.serviceType === ServiceTypeEnum.Maintenance
    ) || {};
  const { enabled: isGuestsEnabled = false } =
    serviceTypes.find(
      (service: BuildingServiceType) =>
        service.serviceType === ServiceTypeEnum.MyGuests
    ) || {};
  const { enabled: isCommFeedEnabled = false } =
    serviceTypes.find(
      (service: BuildingServiceType) =>
        service.serviceType === ServiceTypeEnum.CommFeed
    ) || {};
  const { enabled: isFitnessEnabled = false } =
    serviceTypes.find(
      (service: BuildingServiceType) =>
        service.serviceType === ServiceTypeEnum.Fitness
    ) || {};
  const { enabled: isCommSurveyEnabled = false } =
    serviceTypes.find(
      (service: BuildingServiceType) =>
        service.serviceType === ServiceTypeEnum.CommSurvey
    ) || {};
  const { enabled: isResidentFeedbackEnabled = false } =
    serviceTypes.find(
      (service: BuildingServiceType) =>
        service.serviceType === ServiceTypeEnum.ResidentFeedback
    ) || {};
  const isResidentPollsEnabled =
    serviceTypes.find(
      (service) => service.serviceType === ServiceTypeEnum.ResidentPolls
    )?.enabled ?? false;
  const isAmenifyEnabled =
    serviceTypes.find(
      (service) => service.serviceType === ServiceTypeEnum.Amenify
    )?.enabled ?? false;
  const { enabled: isBiltRewardsEnabled = false } =
    serviceTypes.find(
      (service: BuildingServiceType) =>
        service.serviceType === ServiceTypeEnum.BiltRewards
    ) || {};

  const isOnboardingAmenitiesEnabled = useGetOnboardingAmenitiesEnabled();
  const isOnboardingComplete =
    onboardingData.length > 0
      ? onboardingData
          .filter((obs) => obs.enabled)
          .every((obs) => obs.isCompleted || obs.isSkipped)
      : false;

  if (user.status === USER_STATUS.CURRENT || isOnboardingComplete) {
    navItems.push({ label: "Home", to: "home", icon: "home-alt" });
  }
  if (
    user.status === USER_STATUS.APPLICANT &&
    !isStandaloneFutureResident(user)
  ) {
    navItems.push({
      label: "Checklist",
      to: `checklist/${encodeId(user.userId)}`,
      icon: "list-check"
    });
  }

  if (
    !isOnboardingComplete &&
    user.status === USER_STATUS.APPLICANT &&
    !isStandaloneFutureResident(user)
  ) {
    return navItems;
  } else {
    if (isAmenitiesEnabled) {
      navItems.push({
        label: "Amenities",
        to: `amenities/${encodeId(user.propertyId)}`,
        icon: "umbrella-beach"
      });
    }
    if (isEventsEnabled) {
      navItems.push({
        label: eventsDisplayName,
        to: `events/property/${encodeId(user.propertyId)}`,
        icon: "glass-cheers"
      });
    }
    if (isFitnessEnabled) {
      navItems.push({
        label: "Fitness",
        to: `fitness/${encodeId(user.propertyId)}`,
        icon: "dumbbell"
      });
    }

    if (isMaintenanceEnabled) {
      navItems.push({
        label: maintenanceDisplayName || "Maintenance",
        to: `maintenance/${encodeId(user.propertyId)}`,
        icon: "tools"
      });
    }
    if (isAmenitiesEnabled || isEventsEnabled || isOnboardingAmenitiesEnabled) {
      navItems.push({
        label: "Bookings",
        to: "bookings",
        icon: "calendar-plus"
      });
    }
    if (isBuildingOffersEnabled) {
      navItems.push({
        label: marketPlaceDisplayName || "Building Offers",
        to: "marketplace",
        icon: "tag"
      });
    }
    if (user.status !== USER_STATUS.APPLICANT && isRentEnabled) {
      navItems.push({
        label: rentDisplayName || "Rent",
        to: `rent/${encodeId(user.userId)}`,
        icon: "sack-dollar"
      });
    }
    if (isGuestsEnabled) {
      navItems.push({ label: "Guests", to: "guests", icon: "address-book" });
    }
    if (isCommFeedEnabled) {
      navItems.push({
        label: "Community Feed",
        to: "community-feed",
        icon: "comment-alt-check"
      });
    }
    if (isResidentFeedbackEnabled || isResidentPollsEnabled) {
      navItems.push({
        label: "My Feedback",
        to: "my-feedback",
        icon: ["far", "message-dots"]
      });
    }
    if (isAmenifyEnabled) {
      navItems.push({
        label: "Amenify",
        to: "amenify",
        icon: "store",
        image: "/amenify.png"
      });
    }

    if (isBiltRewardsEnabled && biltRewardsFeatureToggle) {
      navItems.push({
        label: "Bilt Rewards",
        to: "bilt-rewards",
        icon: "tag"
      });
    }

    return navItems;
  }
}
